/**
 * ******************************************************
 * Copyright (C) 2016-2020 VMware, Inc. All rights reserved.
 * *******************************************************
 *
 * @format
 */

/**
 * @fileoverview devicemessagemanager.js -- DeviceMessageManager
 * Class contains the logic for device related messages, mainly works
 * as a status converter since we need to mock response to avoid pending
 * agent response for too long when agent ask whether has devices.
 * For bug 2629754, queue all events at client side while pending on
 * device permission asking and also encoder initing and to enforce messages
 * are processed by order.
 */

/**
 * Class contains the main logic of logic for device related messages
 * @type {function}
 */

import RTAV from "./logservice";

export default function DeviceMessageManager(controller) {
   let unprocessedActionQueue,
      processingAction,
      permissionDialogPending,
      allowedActions,
      enableDeviceActions,
      localMessageController,
      messageWaitManager;

   let init = function(controller, self) {
      unprocessedActionQueue = [];
      processingAction = null;
      // for UI only
      permissionDialogPending = {
         audio: false,
         video: false
      };
      allowedActions = [
         "PMsgStop_A",
         "PMsgStart_A",
         "PMsgStop_V",
         "PMsgStart_V",
         "PMsgStartStream"
      ];
      // used for permission status check
      enableDeviceActions = {
         audio: "PMsgStart_A",
         video: "PMsgStart_V"
      };

      localMessageController = controller;
      localMessageController.deviceMessageManager = self;
      messageWaitManager = new RTAV.MessageWaitManager();
   };

   let isProcessingDeviceEnable = function(type) {
      return processingAction &&
         processingAction.messageType === enableDeviceActions[type];
   };

   let hasEnableActionInQueue = function(type) {
      return unprocessedActionQueue.some((action) => {
         return enableDeviceActions[type] === action.messageType;
      });
   };

   let hasUnfinishedTask = function() {
      return unprocessedActionQueue.length > 0 || processingAction;
   };

   let pushUnprocessedAction = function(action) {
      unprocessedActionQueue.push(action);
      RTAV.logService.debug(
         "unprocessed action queue updated to: " +
            JSON.stringify(unprocessedActionQueue)
      );
   };

   /**
    * Add no cancellation in queue, since client had remove the parallel
    * processing with mocked response.
    */
   let popActionFromQueue = function() {
      if (unprocessedActionQueue.length > 0) {
         let nextAction = unprocessedActionQueue.splice(0, 1)[0];
         RTAV.logService.debug("pop action: " + JSON.stringify(nextAction));
         return nextAction;
      }
      return null;
   };

   let executeNextAction = function(self) {
      processingAction = null;
      let nextAction = popActionFromQueue();
      if (nextAction) {
         processMessage(
            self,
            nextAction.messageType,
            nextAction.callback,
            nextAction.param
         );
      }
   };

   let processMessage = function(self, messageType, callback, param) {
      if (!localMessageController) {
         RTAV.logService.devError("uninited controller");
         return;
      }
      processingAction = {
         messageType: messageType,
         callback: callback,
         param: param
      };
      RTAV.logService.debug(
         "push device message into wait queue: " + messageType
      );
      messageWaitManager.process(messageType, function() {
         RTAV.logService.log("processing device message: " + messageType);
         switch (messageType) {
            case "PMsgStart_A":
               localMessageController.enableAudioIn(function(success) {
                  if (success) {
                     RTAV.logService.debug("audio device successfully enabled");
                  } else {
                     RTAV.logService.debug("audio device fail to be enabled");
                  }
                  callback(success);
                  executeNextAction(self);
               }, self);
               break;
            case "PMsgStop_A":
               localMessageController.disableAudioIn(function(success) {
                  if (success) {
                     RTAV.logService.debug(
                        "audio device successfully disabled"
                     );
                  } else {
                     RTAV.logService.debug("audio device fail to be disabled");
                  }
                  callback(success);
                  executeNextAction(self);
               });
               break;
            case "PMsgStart_V":
               localMessageController.enableVideoIn(function(success) {
                  if (success) {
                     RTAV.logService.debug("video device successfully enabled");
                  } else {
                     RTAV.logService.debug("video device fail to be enabled");
                  }
                  callback(success);
                  executeNextAction(self);
               }, self);
               break;
            case "PMsgStop_V":
               localMessageController.disableVideoIn(function(success) {
                  if (success) {
                     RTAV.logService.debug(
                        "video device successfully disabled"
                     );
                  } else {
                     RTAV.logService.debug("video device fail to be disabled");
                  }
                  callback(success);
                  executeNextAction(self);
               });
               break;
            case "PMsgStartStream":
               localMessageController.startMediaIn(param, callback);
               setTimeout(() => {
                  executeNextAction(self);
               }, 1000);
               break;
            default:
               RTAV.logService.devError(
                  "unexpected message type in messageWaitManager.process"
               );
               executeNextAction();
               break;
         }
      });
   };

   let processAction = function(self, messageType, callback, param) {
      if (hasUnfinishedTask()) {
         RTAV.logService.warn(
            "has unprocessed message push into the queue for later execution"
         );
         pushUnprocessedAction({
            messageType: messageType,
            callback: callback,
            prarm: param
         });
         RTAV.logService.debug(
            "message added: " +
               messageType +
               ", processing: " +
               processingAction.messageType
         );
      } else {
         processMessage(self, messageType, callback, param);
      }
   };

   let self = {
      /**
       * should be called when get device related message
       * @param {string} messageType Message name
       * @param {function} callback Optimal The callback should be called with
       *     bool indicate whether success
       */
      onMessage: function(messageType, callback, param) {
         if (typeof callback !== "function") {
            RTAV.logService.devError(
               "inner error: there is no callback for " + messageType
            );
            return;
         }
         if (!allowedActions.includes(messageType)) {
            RTAV.logService.devError(
               "DeviceMessageManager shouldn't be used to process message " +
                  messageType
            );
            callback(false);
            return;
         }
         localMessageController.processWhenHaveResources(
            function(canProcess) {
               if (canProcess) {
                  processAction(this, messageType, callback, param);
               } else {
                  callback(false);
                  RTAV.logService.log(
                     "do nothing for " +
                        messageType +
                        " since session is not allow to be processed"
                  );
               }
            }.bind(this)
         );
      },

      /**
       * function should be called when premission is handled
       * @param {string} type Of audio or video indicate which premission is handled
       */
      onPermissionHandled: function(type) {
         RTAV.logService.trace("premission dialog is closed");
         if (!permissionDialogPending[type]) {
            RTAV.logService.devError(
               "the inner logic is wrong, the premission dialog seems get disapper"
            );
            return;
         }
         permissionDialogPending[type] = false;
      },

      /**
       * function should be called when premission is asked
       * @param {string} type Of audio or video indicate which premission is asked
       */
      onPermissionAsked: function(type) {
         if (permissionDialogPending[type]) {
            RTAV.logService.devError(
               "the inner logic is wrong, the premission dialog seems get duplicated"
            );
            return;
         }
         permissionDialogPending[type] = true;
         RTAV.resourceManager.emitDeviceStatusChanged();
         RTAV.logService.trace("premission dialog poped out");
      },

      /**
       * function to known whether premission is being asked for specified type
       * @param {string} type Of audio or video indicate which premission is asked
       * @return {boolean} This returns whether premission is being asked for
       *     specified type
       */
      isPermissionPending: function(type) {
         if (isProcessingDeviceEnable(type) || hasEnableActionInQueue(type)) {
            RTAV.logService.trace(
               "the premission dialog of type " +
                  type +
                  " is hold so treat as pending"
            );
            return true;
         }
         if (type !== undefined) {
            return permissionDialogPending[type];
         }
         return permissionDialogPending["audio"] || permissionDialogPending["video"];
      }
   };
   init(controller, self);
   return self;
}
